import React, { useState } from "react";

/**
 * Category-aware destination-card placeholder.
 *
 * A card without a `featured_image` / non-empty `images[]` renders one
 * of three branded placeholders (hotel / resort / camping) plus a
 * disclosure overlay:
 *
 *   "Not a true photo of this location — photo coming soon"
 *
 * For every other category (Cruises, Destinations, Activities, …) —
 * or when the card has no category at all — we render a solid dark
 * card (`tb-navy-deep`) with the same overlay. This is a **rendering
 * concern only**; the DB doc is never mutated. `has_featured_image`
 * / coverage stats reflect what's actually stored, not what's shown.
 *
 * Real-image failure mode: if the `<img>` fires `onError` (broken
 * URL, CORS, 404, …) we swap to the placeholder path so users never
 * see the ugly broken-image icon.
 *
 * Placeholders live in `frontend/public/placeholders/` and are
 * checked into the repo — do NOT hotlink to Emergent CDN.
 */

// Canonical destination filter names (mirror of the admin catalog set)
// mapped to their placeholder image. Values are absolute paths served
// by CRA out of `public/`.
const PLACEHOLDER_HOTEL = "/placeholders/placeholder_hotel.png";
const PLACEHOLDER_RESORT = "/placeholders/placeholder_resort.png";
const PLACEHOLDER_CAMPING = "/placeholders/placeholder_camping.png";

const CATEGORY_TO_PLACEHOLDER = {
  // Hotel-ish
  "hotels": PLACEHOLDER_HOTEL,
  "hotel": PLACEHOLDER_HOTEL,
  "lodges/inns/b&bs": PLACEHOLDER_HOTEL,
  "lodge": PLACEHOLDER_HOTEL,
  "inn": PLACEHOLDER_HOTEL,
  "b&b": PLACEHOLDER_HOTEL,
  // Resort-ish
  "resorts": PLACEHOLDER_RESORT,
  "resort": PLACEHOLDER_RESORT,
  "all-inclusive resorts": PLACEHOLDER_RESORT,
  "all-inclusive resort": PLACEHOLDER_RESORT,
  // Camping-ish
  "camping/campgrounds": PLACEHOLDER_CAMPING,
  "camping": PLACEHOLDER_CAMPING,
  "campground": PLACEHOLDER_CAMPING,
  "campgrounds": PLACEHOLDER_CAMPING,
  "rv resorts": PLACEHOLDER_CAMPING,
  "rv resort": PLACEHOLDER_CAMPING,
};

const OVERLAY_TEXT = "Not a true photo of this location \u2014 photo coming soon";

function pickPlaceholder(card) {
  // Prefer the canonical `primary_filter` field the catalog uses,
  // fall back to `content_type` / `category` / `type` for older /
  // partial schemas so the mapping never silently misses.
  const raw = (
    card?.primary_filter
    || card?.content_type
    || card?.category
    || card?.type
    || ""
  );
  const key = String(raw).trim().toLowerCase();
  const src = CATEGORY_TO_PLACEHOLDER[key] || null;
  return { src, isGrey: !src };
}

function firstTruthyImage(card) {
  return (
    card?.featured_image
    || (Array.isArray(card?.images) ? card.images[0] : null)
    || card?.image
    || card?.cover_image
    || card?.cover_image_url
    || null
  );
}

/**
 * Overlay caption. Rendered on top of both the placeholder photo AND
 * the grey-background variant so users always see the disclosure.
 * `size` = "sm" (card list, default) | "lg" (detail hero).
 */
function PlaceholderOverlay({ size = "sm" }) {
  const sizeCls =
    size === "lg" ? "text-base sm:text-lg px-6" : "text-[11px] sm:text-xs px-3";
  return (
    <div
      data-testid="card-image-placeholder-overlay"
      className={
        "absolute inset-0 flex items-center justify-center text-center pointer-events-none " +
        sizeCls
      }
    >
      <span
        className="font-serif-it italic text-tb-cream/95 leading-snug"
        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}
      >
        {OVERLAY_TEXT}
      </span>
    </div>
  );
}

/**
 * <CardImage />
 *
 *  - `card` — destination-shaped object (accepts `featured_image` /
 *    `images` / `image` / `cover_image` / `cover_image_url` / any
 *    combination; category from `primary_filter` / `content_type` /
 *    `category` / `type`).
 *  - `className` — passed straight to the outer wrapper so callers
 *    keep their existing sizing/aspect classes.
 *  - `showPlaceholderText` — set to `false` to suppress the overlay
 *    caption (e.g. on very small chip thumbnails where it wouldn't be
 *    legible).
 *  - `size` — overlay text size preset (`sm` / `lg`).
 *  - `imgClassName` — optional override for the inner `<img>` when a
 *    caller needs to inject its own object-fit / border-radius.
 *  - Extra props (`draggable`, `loading`, …) pass through to `<img>`.
 */
export default function CardImage({
  card,
  className = "",
  showPlaceholderText = true,
  size = "sm",
  imgClassName = "w-full h-full object-cover",
  alt,
  ...imgProps
}) {
  const originalSrc = firstTruthyImage(card);
  const [broken, setBroken] = useState(false);
  const showReal = !!originalSrc && !broken;
  const { src: placeholderSrc, isGrey } = pickPlaceholder(card);
  const label = alt ?? card?.name ?? "";

  return (
    <div
      data-testid="card-image"
      data-has-image={showReal ? "true" : "false"}
      data-placeholder-kind={showReal ? "none" : (isGrey ? "grey" : "photo")}
      className={"relative overflow-hidden " + (isGrey && !showReal ? "bg-tb-navy-deep " : "") + className}
    >
      {showReal ? (
        <img
          src={originalSrc}
          alt={label}
          className={imgClassName}
          onError={() => setBroken(true)}
          {...imgProps}
        />
      ) : placeholderSrc ? (
        <img
          src={placeholderSrc}
          alt={label}
          aria-hidden="true"
          className={imgClassName}
          draggable={false}
        />
      ) : (
        // Solid grey card — no photo — for categories that don't have
        // a branded placeholder. Overlay text lives on top.
        <div className="absolute inset-0 bg-tb-navy-deep" aria-hidden="true" />
      )}
      {!showReal && showPlaceholderText && <PlaceholderOverlay size={size} />}
    </div>
  );
}

// Named export for callers that want to build custom layouts.
export { pickPlaceholder, firstTruthyImage, PLACEHOLDER_HOTEL, PLACEHOLDER_RESORT, PLACEHOLDER_CAMPING };
